import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import { useUrlGenerator } from '@panneau/core/contexts';
import _createClass from '@babel/runtime/helpers/createClass';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _callSuper from '@babel/runtime/helpers/callSuper';
import _inherits from '@babel/runtime/helpers/inherits';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { getJSON, getCSRFHeaders, postJSON } from '@folklore/fetch';
import { generatePath } from '@folklore/routes';
import queryString from 'query-string';
import { useQuery, keepPreviousData, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import isEmpty from 'lodash-es/isEmpty';

var useData = function useData(loadData) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$initialData = _ref.initialData,
    initialData = _ref$initialData === void 0 ? null : _ref$initialData,
    _ref$autoload = _ref.autoload,
    autoload = _ref$autoload === void 0 ? true : _ref$autoload;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  var _useState5 = useState(initialData),
    _useState6 = _slicedToArray(_useState5, 2),
    data = _useState6[0],
    setData = _useState6[1];
  var load = useCallback(function () {
    var canceled = false;
    setLoading(true);
    var promise = loadData.apply(void 0, arguments).then(function (newData) {
      if (!canceled) {
        setData(newData);
        setLoading(false);
      }
      return newData;
    })["catch"](function (newError) {
      setError(newError);
      setLoading(false);
    });
    promise.cancel = function () {
      canceled = true;
      setLoading(false);
    };
    return promise;
  }, [loadData, setLoading, setData]);
  useEffect(function () {
    var loader = null;
    if (autoload) {
      loader = load();
    }
    return function () {
      if (loader !== null) {
        loader.cancel();
      }
    };
  }, [autoload, load]);
  return {
    data: data,
    load: load,
    loading: loading,
    error: error
  };
};

var _excluded$3 = ["store", "url", "paginated", "query", "onQueryChange", "queryConfig", "keepData"],
  _excluded2$1 = ["page", "count"],
  _excluded3 = ["data", "refetch", "isLoading", "isFetching", "isRefetching", "isFetched", "status", "error"];

// For backwards compatibility

var useItems$1 = function useItems(_ref) {
  var store = _ref.store,
    url = _ref.url,
    _ref$paginated = _ref.paginated,
    initialPaginated = _ref$paginated === void 0 ? false : _ref$paginated,
    _ref$query = _ref.query,
    initialQuery = _ref$query === void 0 ? null : _ref$query,
    onQueryChange = _ref.onQueryChange,
    _ref$queryConfig = _ref.queryConfig,
    queryConfig = _ref$queryConfig === void 0 ? null : _ref$queryConfig,
    _ref$keepData = _ref.keepData,
    keepData = _ref$keepData === void 0 ? true : _ref$keepData,
    oldProps = _objectWithoutProperties(_ref, _excluded$3);
  // Legacy compatibility
  var _ref2 = oldProps || {},
    _ref2$getPage = _ref2.getPage,
    getPage = _ref2$getPage === void 0 ? null : _ref2$getPage,
    _ref2$getItems = _ref2.getItems,
    getItems = _ref2$getItems === void 0 ? null : _ref2$getItems,
    _ref2$page = _ref2.page,
    oldPage = _ref2$page === void 0 ? null : _ref2$page,
    _ref2$count = _ref2.count,
    oldCount = _ref2$count === void 0 ? null : _ref2$count;
    _ref2.items;
    var _ref2$pages = _ref2.pages,
    initialPages = _ref2$pages === void 0 ? null : _ref2$pages,
    _ref2$getPageFromResp = _ref2.getPageFromResponse,
    getPageFromResponse = _ref2$getPageFromResp === void 0 ? function () {
      var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref3$pagination = _ref3.pagination,
        _ref3$pagination$page = _ref3$pagination.page,
        currentPage = _ref3$pagination$page === void 0 ? null : _ref3$pagination$page,
        _ref3$pagination$last = _ref3$pagination.last_page,
        lastPage = _ref3$pagination$last === void 0 ? null : _ref3$pagination$last,
        _ref3$pagination$tota = _ref3$pagination.total,
        total = _ref3$pagination$tota === void 0 ? null : _ref3$pagination$tota,
        _ref3$pagination$per_ = _ref3$pagination.per_page,
        perPage = _ref3$pagination$per_ === void 0 ? null : _ref3$pagination$per_,
        _ref3$data = _ref3.data,
        items = _ref3$data === void 0 ? null : _ref3$data;
      return {
        page: parseInt(currentPage, 10),
        lastPage: parseInt(lastPage, 10),
        total: parseInt(total, 10),
        count: parseInt(perPage, 10),
        items: items
      };
    } : _ref2$getPageFromResp,
    _ref2$getItemsFromRes = _ref2.getItemsFromResponse,
    getItemsFromResponse = _ref2$getItemsFromRes === void 0 ? function (data) {
      return data;
    } : _ref2$getItemsFromRes,
    _ref2$onItemsLoaded = _ref2.onItemsLoaded,
    onItemsLoaded = _ref2$onItemsLoaded === void 0 ? null : _ref2$onItemsLoaded,
    _ref2$onPageLoaded = _ref2.onPageLoaded,
    onPageLoaded = _ref2$onPageLoaded === void 0 ? null : _ref2$onPageLoaded,
    _ref2$onLoaded = _ref2.onLoaded,
    onLoaded = _ref2$onLoaded === void 0 ? null : _ref2$onLoaded,
    _ref2$onError = _ref2.onError,
    onError = _ref2$onError === void 0 ? null : _ref2$onError;
  var paginated = useMemo(function () {
    return getPage !== null || initialPages !== null || initialPaginated;
  }, [getPage, initialPages, initialPaginated]);
  var query = useMemo(function () {
    // Backwards compatible
    if (oldPage !== null || oldCount !== null) {
      return _objectSpread({
        page: oldPage,
        count: oldCount
      }, initialQuery);
    }
    return initialQuery;
  }, [initialQuery]);
  var _ref4 = query || {},
    _ref4$page = _ref4.page,
    page = _ref4$page === void 0 ? null : _ref4$page,
    _ref4$count = _ref4.count,
    count = _ref4$count === void 0 ? null : _ref4$count,
    queryWithoutPageAndCount = _objectWithoutProperties(_ref4, _excluded2$1);
  var queryKey = useMemo(function () {
    return paginated && getPage !== null ? [store, page, count || 10, queryWithoutPageAndCount] : [store, query];
  }, [paginated, page, count, queryWithoutPageAndCount]);
  var _useQuery = useQuery(_objectSpread(_objectSpread(_objectSpread({
      queryKey: queryKey,
      queryFn: function queryFn(_ref5) {
        var key = _ref5.queryKey;
        if (paginated && getPage !== null) {
          var _key = _slicedToArray(key, 4),
            _key$ = _key[1],
            p = _key$ === void 0 ? null : _key$,
            _key$2 = _key[2],
            c = _key$2 === void 0 ? null : _key$2,
            _key$3 = _key[3],
            _q = _key$3 === void 0 ? null : _key$3;
          return getPage(p, c, _q);
        }
        if (!paginated && getItems !== null) {
          var _key2 = _slicedToArray(key, 2),
            _key2$ = _key2[1],
            _q2 = _key2$ === void 0 ? null : _key2$;
          return getItems(_q2);
        }
        var _key3 = _slicedToArray(key, 2),
          _key3$ = _key3[1],
          q = _key3$ === void 0 ? null : _key3$;
        return getJSON("".concat(url).concat(!isEmpty(q) ? "?".concat(queryString.stringify(q, {
          arrayFormat: 'bracket'
        })) : ''));
      }
    }, keepData ? {
      placeholderData: keepPreviousData
    } : null), queryConfig), {}, {
      refetchOnMount: 'always'
    })),
    _useQuery$data = _useQuery.data,
    data = _useQuery$data === void 0 ? null : _useQuery$data,
    reload = _useQuery.refetch,
    isLoading = _useQuery.isLoading,
    isFetching = _useQuery.isFetching,
    isRefetching = _useQuery.isRefetching,
    isFetched = _useQuery.isFetched,
    _useQuery$status = _useQuery.status,
    status = _useQuery$status === void 0 ? null : _useQuery$status,
    _useQuery$error = _useQuery.error,
    error = _useQuery$error === void 0 ? null : _useQuery$error,
    otherProps = _objectWithoutProperties(_useQuery, _excluded3);
  var _ref6 = data || {},
    _ref6$data = _ref6.data,
    items = _ref6$data === void 0 ? [] : _ref6$data,
    _ref6$pagination = _ref6.pagination,
    pagination = _ref6$pagination === void 0 ? null : _ref6$pagination,
    _ref6$meta = _ref6.meta,
    meta = _ref6$meta === void 0 ? null : _ref6$meta;
  var _ref7 = pagination || meta || {},
    _ref7$page = _ref7.page,
    currentPage = _ref7$page === void 0 ? null : _ref7$page,
    _ref7$last_page = _ref7.last_page,
    lastPage = _ref7$last_page === void 0 ? null : _ref7$last_page,
    _ref7$total = _ref7.total,
    total = _ref7$total === void 0 ? null : _ref7$total;
  var loadNextPage = useCallback(function () {
    if (onQueryChange !== null && paginated && page !== null && page < lastPage) {
      onQueryChange(_objectSpread(_objectSpread({}, query), {}, {
        page: page + 1
      }));
    }
  }, [paginated, pagination, page]);
  var loadPage = useCallback(function (newPage) {
    if (onQueryChange !== null && newPage !== null) {
      onQueryChange(_objectSpread(_objectSpread({}, query), {}, {
        page: newPage
      }));
    }
  }, [query, onQueryChange]);

  // Keep a list of updated items
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    updatedItems = _useState2[0],
    setUpdatedItems = _useState2[1];
  var updateItem = useCallback(function (item) {
    var _ref8 = item || {},
      _ref8$id = _ref8.id,
      itemId = _ref8$id === void 0 ? null : _ref8$id;
    if (itemId !== null) {
      setUpdatedItems([].concat(_toConsumableArray((updatedItems || []).filter(function () {
        var _ref9 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref9$id = _ref9.id,
          id = _ref9$id === void 0 ? null : _ref9$id;
        return id !== itemId;
      })), [item]));
    }
  }, [updatedItems, setUpdatedItems]);
  var replaceUpdatedItems = useCallback(function (currentItems) {
    if (currentItems === null || updatedItems === null || updatedItems.length === 0) {
      return currentItems;
    }
    return (currentItems || []).map(function (item) {
      var _ref10 = item || {},
        _ref10$id = _ref10.id,
        itemId = _ref10$id === void 0 ? null : _ref10$id;
      var updated = (updatedItems || []).find(function () {
        var _ref11 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref11$id = _ref11.id,
          id = _ref11$id === void 0 ? null : _ref11$id;
        return id === itemId;
      }) || null;
      if (updated !== null) {
        return updated;
      }
      return item;
    }, []);
  }, [updatedItems]);
  var partialItems = useMemo(function () {
    if (data === null) {
      return data;
    }
    if (paginated) {
      if (getPageFromResponse !== null) {
        return getPageFromResponse(items);
      }
      return items;
    }
    if (getItemsFromResponse !== null) {
      return getItemsFromResponse(data);
    }
    return data;
  }, [items, data, paginated, getPageFromResponse, getItemsFromResponse, updatedItems]);
  var finalItems = replaceUpdatedItems(partialItems);

  // Pseudo-events for compatibility
  useEffect(function () {
    if (status === 'success') {
      if (onPageLoaded !== null && paginated) {
        onItemsLoaded(data);
      }
      if (onItemsLoaded !== null && !paginated) {
        onItemsLoaded(data);
      }
      if (onLoaded !== null) {
        onLoaded(data);
      }
    }
    if (status === 'error' && onError !== null) {
      onError(error);
    }
  }, [status]);

  // Keep a list of pages, useEffect wont work here because delayed
  var pages = useRef(null);
  if (isFetched && page !== null && data !== null) {
    pages.current = _objectSpread(_objectSpread({}, pages.current), {}, _defineProperty({}, page, data));
  }
  var allItems = pages.current !== null ? replaceUpdatedItems(Object.keys(pages.current).flatMap(function (k) {
    var _pages$current$k;
    return (_pages$current$k = pages.current[k]) === null || _pages$current$k === void 0 ? void 0 : _pages$current$k.data;
  })) : null;
  var finalLoading = isLoading || isFetching || isRefetching;
  return _objectSpread(_objectSpread({}, otherProps), {}, {
    items: finalItems,
    allItems: allItems,
    pages: pages.current,
    pageItems: paginated ? finalItems : null,
    pagination: paginated ? {
      page: currentPage,
      lastPage: lastPage,
      total: total
    } : null,
    total: total,
    lastPage: lastPage,
    loaded: isFetched,
    allLoaded: paginated && pages.current !== null && Object.keys(pages.current).length === lastPage || !paginated && isFetched,
    // very basic stuff, TODO
    loading: finalLoading,
    loadNextPage: loadNextPage,
    loadPage: loadPage,
    reloadPage: reload,
    reload: reload,
    reset: reload,
    updateItem: updateItem,
    status: status,
    error: error
  });
};

var useItems = function useItems(_ref) {
  var _ref$getPage = _ref.getPage,
    getPage = _ref$getPage === void 0 ? null : _ref$getPage,
    _ref$getItems = _ref.getItems,
    getItems = _ref$getItems === void 0 ? null : _ref$getItems,
    _ref$page = _ref.page,
    page = _ref$page === void 0 ? null : _ref$page,
    _ref$count = _ref.count,
    count = _ref$count === void 0 ? 10 : _ref$count,
    _ref$items = _ref.items,
    providedItems = _ref$items === void 0 ? null : _ref$items,
    _ref$pages = _ref.pages,
    initialPages = _ref$pages === void 0 ? null : _ref$pages,
    _ref$getPageFromRespo = _ref.getPageFromResponse,
    getPageFromResponse = _ref$getPageFromRespo === void 0 ? function (_ref2) {
      var _ref2$pagination = _ref2.pagination,
        currentPage = _ref2$pagination.page,
        lastPage = _ref2$pagination.last_page,
        total = _ref2$pagination.total,
        perPage = _ref2$pagination.per_page,
        items = _ref2.data;
      return {
        page: parseInt(currentPage, 10),
        lastPage: parseInt(lastPage, 10),
        total: parseInt(total, 10),
        count: parseInt(perPage, 10),
        items: items
      };
    } : _ref$getPageFromRespo,
    _ref$getItemsFromResp = _ref.getItemsFromResponse,
    getItemsFromResponse = _ref$getItemsFromResp === void 0 ? function (data) {
      return data;
    } : _ref$getItemsFromResp,
    _ref$onItemsLoaded = _ref.onItemsLoaded,
    onItemsLoaded = _ref$onItemsLoaded === void 0 ? null : _ref$onItemsLoaded,
    _ref$onPageLoaded = _ref.onPageLoaded,
    onPageLoaded = _ref$onPageLoaded === void 0 ? null : _ref$onPageLoaded,
    _ref$onLoaded = _ref.onLoaded,
    onLoaded = _ref$onLoaded === void 0 ? null : _ref$onLoaded,
    _ref$onError = _ref.onError,
    onError = _ref$onError === void 0 ? null : _ref$onError;
  var isPaginated = getPage !== null || initialPages !== null;
  var lastState = useRef(null);
  var initialState = useMemo(function () {
    var finalInitialPages = initialPages !== null ? initialPages.map(function (it) {
      return getPageFromResponse(it);
    }) : null;
    return {
      lastPage: finalInitialPages !== null ? finalInitialPages.reduce(function (currentLastPage, _ref3) {
        var initialLastPage = _ref3.lastPage;
        return initialLastPage > currentLastPage ? initialLastPage : currentLastPage;
      }, -1) : -1,
      total: finalInitialPages !== null ? finalInitialPages[0].total : (providedItems || []).length,
      loaded: providedItems !== null,
      loading: false,
      pages: finalInitialPages !== null ? finalInitialPages : null,
      items: null
    };
  }, [initialPages, providedItems]);
  var _useState = useState(initialState),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  var lastPage = state.lastPage,
    loaded = state.loaded,
    loading = state.loading,
    stateItems = state.items,
    pages = state.pages,
    total = state.total;
  var items = providedItems || (isPaginated && pages !== null ? pages.reduce(function (pagesItems, _ref4) {
    var pageItems = _ref4.items;
    return pagesItems.concat(pageItems);
  }, []) : stateItems) || null;
  var updateState = function updateState(update) {
    return setState(_objectSpread(_objectSpread({}, state), update));
  };
  var updateFromResponse = function updateFromResponse(response) {
    var error = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var reset = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (error !== null) {
      updateState({
        loaded: false,
        loading: false
      });
      throw error;
    }
    if (isPaginated) {
      var newPage = getPageFromResponse(response);
      var newPages = (reset ? [newPage] : [].concat(_toConsumableArray((pages || []).filter(function (it) {
        return it.page !== newPage.page;
      })), [newPage])).sort(function (a, b) {
        if (a === b) {
          return 0;
        }
        return a > b ? 1 : -1;
      });
      updateState({
        loaded: true,
        loading: false,
        lastPage: newPage.lastPage,
        total: newPage.total,
        pages: newPages
      });
      return newPage;
    }
    var newItems = _toConsumableArray(getItemsFromResponse(response));
    updateState({
      loaded: true,
      loading: false,
      items: newItems,
      total: newItems.length
    });
    return newItems;
  };
  var getNextPage = function getNextPage() {
    var allPages = lastPage !== -1 ? Array.call.apply(Array, [null].concat(_toConsumableArray(Array(lastPage)))).map(function (it, index) {
      return index + 1;
    }) : [];
    var remainingPages = allPages.filter(function (pageNumber) {
      return pages.findIndex(function (it) {
        return it.page === pageNumber;
      }) === -1;
    });
    var firstItem = remainingPages.length > 0 ? remainingPages.shift() : null;
    return firstItem !== null ? firstItem : null;
  };
  var loadItems = function loadItems(requestPage) {
    updateState({
      loading: true
    });
    var canceled = false;
    var request = isPaginated ? getPage(requestPage, count) : getItems();
    var promise = request.then(function (response) {
      return !canceled ? updateFromResponse(response) : Promise.reject();
    })["catch"](function (error) {
      return !canceled ? updateFromResponse(null, error) : Promise.reject();
    }).then(function (response) {
      if (isPaginated && onPageLoaded !== null) {
        onPageLoaded(response);
      } else if (!isPaginated && onItemsLoaded !== null) {
        onItemsLoaded(response);
      }
      if (onLoaded !== null) {
        onLoaded(response);
      }
      return response;
    })["catch"](function (error) {
      if (!canceled && onError !== null) {
        onError(error);
      }
    });
    promise.cancel = function () {
      canceled = true;
    };
    return promise;
  };
  var loadPage = function loadPage(pageToLoad) {
    if (loading) {
      return Promise.reject();
    }
    if (pages.find(function (it) {
      return it.page === pageToLoad;
    }) !== -1) {
      return Promise.reject();
    }
    return loadItems(pageToLoad);
  };
  var loadNextPage = function loadNextPage() {
    if (loading) {
      return Promise.reject();
    }
    var nextPage = getNextPage();
    return nextPage !== null ? loadItems(nextPage) : Promise.resolve();
  };
  var reloadPage = function reloadPage() {
    if (loading) {
      return Promise.reject();
    }
    return loadItems(page);
  };
  var reload = function reload() {
    if (loading) {
      return Promise.reject();
    }
    return loadItems();
  };
  var reset = useCallback(function () {
    setState({
      loaded: false,
      loading: false,
      lastPage: -1,
      total: 0,
      pages: null,
      items: null
    });
  }, [page, setState]);
  var updateItem = function updateItem(newItem) {
    if (loading) {
      return;
    }
    if (isPaginated) {
      var currentPages = pages || [];
      updateState({
        pages: currentPages.map(function (pageData) {
          var pageItems = pageData.items || [];
          if (pageItems.findIndex(function (_ref5) {
            var id = _ref5.id;
            return id === newItem.id;
          }) !== -1) {
            return _objectSpread(_objectSpread({}, pageData), {}, {
              items: pageItems.map(function (it) {
                return it.id === newItem.id ? newItem : it;
              })
            });
          }
          return pageData;
        })
      });
    } else {
      var currentItems = items || [];
      updateState({
        items: currentItems.map(function (it) {
          return it.id === newItem.id ? newItem : it;
        })
      });
    }
  };
  useEffect(function () {
    var hadState = lastState.current !== null;
    lastState.current = initialState;
    if (hadState) {
      setState(initialState);
    }
  }, [initialState]);
  useEffect(function () {
    if (getPage === null && getItems === null || providedItems !== null) {
      return function () {};
    }
    var loadPromise = null;
    var pageToLoad = isPaginated && initialPages === null && page === null ? 1 : page;
    if (!isPaginated || pageToLoad !== null) {
      loadPromise = loadItems(pageToLoad);
    }
    return function () {
      if (loadPromise !== null) {
        loadPromise.cancel();
      }
    };
  }, [getPage, getItems, page]);
  var currentPage = isPaginated && pages !== null ? pages.find(function (_ref6) {
    var pageNumber = _ref6.page;
    return parseInt(pageNumber, 10) === parseInt(page, 10);
  }) || null : null;
  return {
    items: items,
    pages: pages,
    pageItems: currentPage !== null ? currentPage.items : null,
    total: total,
    lastPage: lastPage,
    loaded: loaded,
    allLoaded: !isPaginated && loaded || lastPage !== -1 && isPaginated && pages.length === lastPage,
    loading: loading,
    loadNextPage: loadNextPage,
    loadPage: loadPage,
    reloadPage: reloadPage,
    reload: reload,
    reset: reset,
    updateItem: updateItem
  };
};

var _excluded$2 = ["page", "count"],
  _excluded2 = ["data", "refetch", "isLoading", "isFetching", "isRefetching", "isFetched"];

// The new, better version

var useItemsStore = function useItemsStore(store, _ref) {
  var _ref$url = _ref.url,
    url = _ref$url === void 0 ? null : _ref$url,
    _ref$getPage = _ref.getPage,
    getPage = _ref$getPage === void 0 ? null : _ref$getPage,
    _ref$getItems = _ref.getItems,
    getItems = _ref$getItems === void 0 ? null : _ref$getItems,
    _ref$getPageFromRespo = _ref.getPageFromResponse,
    getPageFromResponse = _ref$getPageFromRespo === void 0 ? null : _ref$getPageFromRespo,
    _ref$getItemsFromResp = _ref.getItemsFromResponse,
    getItemsFromResponse = _ref$getItemsFromResp === void 0 ? null : _ref$getItemsFromResp,
    _ref$page = _ref.page,
    initialPage = _ref$page === void 0 ? null : _ref$page,
    _ref$count = _ref.count,
    initialCount = _ref$count === void 0 ? null : _ref$count,
    _ref$query = _ref.query,
    query = _ref$query === void 0 ? null : _ref$query,
    _ref$onQueryChange = _ref.onQueryChange,
    onQueryChange = _ref$onQueryChange === void 0 ? null : _ref$onQueryChange,
    _ref$queryConfig = _ref.queryConfig,
    queryConfig = _ref$queryConfig === void 0 ? null : _ref$queryConfig,
    _ref$keepData = _ref.keepData,
    keepData = _ref$keepData === void 0 ? true : _ref$keepData;
  var _ref2 = query || {},
    _ref2$page = _ref2.page,
    page = _ref2$page === void 0 ? initialPage : _ref2$page,
    _ref2$count = _ref2.count,
    count = _ref2$count === void 0 ? initialCount : _ref2$count,
    queryWithoutPage = _objectWithoutProperties(_ref2, _excluded$2);
  var paginated = page !== null;
  var queryKey = useMemo(function () {
    return paginated ? [store, page || 1, count || 12, queryWithoutPage] : [store, query];
  }, [paginated, store, page, count, query, queryWithoutPage, getPage, getItems]);
  var _useQuery = useQuery(_objectSpread(_objectSpread(_objectSpread({
      queryKey: queryKey,
      queryFn: function queryFn(_ref3) {
        var _ref3$queryKey = _ref3.queryKey,
          key = _ref3$queryKey === void 0 ? null : _ref3$queryKey;
        if (paginated && getPage !== null) {
          var _key = _slicedToArray(key, 4),
            _key$ = _key[1],
            p = _key$ === void 0 ? null : _key$,
            _key$2 = _key[2],
            c = _key$2 === void 0 ? null : _key$2,
            _key$3 = _key[3],
            _q = _key$3 === void 0 ? null : _key$3;
          return getPage(p, c, _q);
        }
        if (!paginated && getItems !== null) {
          var _key2 = _slicedToArray(key, 2),
            _key2$ = _key2[1],
            _q2 = _key2$ === void 0 ? null : _key2$;
          return getItems(_q2);
        }
        var _key3 = _slicedToArray(key, 2),
          _key3$ = _key3[1],
          q = _key3$ === void 0 ? null : _key3$;
        return getJSON("".concat(url).concat(!isEmpty(q) ? "?".concat(queryString.stringify(q, {
          arrayFormat: 'bracket'
        })) : ''));
      }
    }, keepData ? {
      placeholderData: keepPreviousData
    } : null), queryConfig), {}, {
      refetchOnMount: 'always'
    })),
    _useQuery$data = _useQuery.data,
    data = _useQuery$data === void 0 ? null : _useQuery$data,
    reload = _useQuery.refetch,
    isLoading = _useQuery.isLoading,
    isFetching = _useQuery.isFetching,
    isRefetching = _useQuery.isRefetching,
    isFetched = _useQuery.isFetched,
    otherProps = _objectWithoutProperties(_useQuery, _excluded2);
  var _ref4 = data || {},
    _ref4$data = _ref4.data,
    items = _ref4$data === void 0 ? [] : _ref4$data,
    _ref4$pagination = _ref4.pagination,
    pagination = _ref4$pagination === void 0 ? null : _ref4$pagination,
    _ref4$meta = _ref4.meta,
    meta = _ref4$meta === void 0 ? null : _ref4$meta;
  var _ref5 = pagination || meta || {},
    _ref5$page = _ref5.page,
    currentPage = _ref5$page === void 0 ? null : _ref5$page,
    _ref5$last_page = _ref5.last_page,
    lastPage = _ref5$last_page === void 0 ? 0 : _ref5$last_page,
    _ref5$total = _ref5.total,
    total = _ref5$total === void 0 ? null : _ref5$total;
  var loadNextPage = useCallback(function () {
    if (onQueryChange !== null && paginated && page !== null && page < lastPage) {
      onQueryChange(_objectSpread(_objectSpread({}, query), {}, {
        page: page + 1
      }));
    }
  }, [paginated, pagination, page]);
  var loadPage = useCallback(function (newPage) {
    if (onQueryChange !== null && newPage !== null) {
      onQueryChange(_objectSpread(_objectSpread({}, query), {}, {
        page: newPage
      }));
    }
  }, [query, onQueryChange]);

  // Keep a list of updated items
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    updatedItems = _useState2[0],
    setUpdatedItems = _useState2[1];
  var updateItem = useCallback(function (item) {
    var _ref6 = item || {},
      _ref6$id = _ref6.id,
      itemId = _ref6$id === void 0 ? null : _ref6$id;
    if (itemId !== null) {
      setUpdatedItems([].concat(_toConsumableArray((updatedItems || []).filter(function () {
        var _ref7 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref7$id = _ref7.id,
          id = _ref7$id === void 0 ? null : _ref7$id;
        return id !== itemId;
      })), [item]));
    }
  }, [updatedItems, setUpdatedItems]);
  var replaceUpdatedItems = useCallback(function (currentItems) {
    if (currentItems === null || updatedItems === null || updatedItems.length === 0) {
      return currentItems;
    }
    return (currentItems || []).map(function (item) {
      var _ref8 = item || {},
        _ref8$id = _ref8.id,
        itemId = _ref8$id === void 0 ? null : _ref8$id;
      var updated = (updatedItems || []).find(function () {
        var _ref9 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref9$id = _ref9.id,
          id = _ref9$id === void 0 ? null : _ref9$id;
        return id === itemId;
      }) || null;
      if (updated !== null) {
        return updated;
      }
      return item;
    }, []);
  }, [updatedItems]);
  var partialItems = useMemo(function () {
    if (data === null) {
      return data;
    }
    if (paginated) {
      if (getPageFromResponse !== null) {
        return getPageFromResponse(items);
      }
      return items;
    }
    if (getItemsFromResponse !== null) {
      return getItemsFromResponse(data);
    }
    return data;
  }, [items, data, paginated, getPageFromResponse, getItemsFromResponse, updatedItems]);
  var finalItems = replaceUpdatedItems(partialItems);

  // Keep a list of pages, useEffect wont work here because delayed
  var pages = useRef(null);
  if (isFetched && page !== null && data !== null) {
    pages.current = _objectSpread(_objectSpread({}, pages.current), {}, _defineProperty({}, page, data));
  }
  var allItems = pages.current !== null ? replaceUpdatedItems(Object.keys(pages.current).flatMap(function (k) {
    var _pages$current$k;
    return (_pages$current$k = pages.current[k]) === null || _pages$current$k === void 0 ? void 0 : _pages$current$k.data;
  })) : finalItems;
  var finalLoading = isLoading || isFetching || isRefetching;
  return _objectSpread(_objectSpread({}, otherProps), {}, {
    isLoading: isLoading,
    isFetching: isFetching,
    isRefetching: isRefetching,
    isFetched: isFetched,
    items: finalItems,
    pages: pages.current,
    allItems: allItems,
    loading: finalLoading,
    loaded: isFetched,
    reload: reload,
    loadPage: loadPage,
    loadNextPage: loadNextPage,
    updateItem: updateItem,
    pagination: paginated ? {
      page: currentPage,
      lastPage: lastPage,
      total: total
    } : null
  });
};

var AccountApi = /*#__PURE__*/function () {
  function AccountApi(api, opts) {
    _classCallCheck(this, AccountApi);
    this.api = api;
    this.options = _objectSpread({
      withCredentials: false
    }, opts);
  }
  return _createClass(AccountApi, [{
    key: "update",
    value: function update(data) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('account'), data, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }, {
    key: "updatePassword",
    value: function updatePassword(data) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('account.password'), data, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }, {
    key: "updateTaxes",
    value: function updateTaxes(data) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('account.taxes'), data, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }, {
    key: "delete",
    value: function _delete(data) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('account.delete'), data, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }]);
}();

var AuthApi = /*#__PURE__*/function () {
  function AuthApi(api, opts) {
    _classCallCheck(this, AuthApi);
    this.api = api;
    this.options = _objectSpread({
      withCredentials: true
    }, opts);
  }
  return _createClass(AuthApi, [{
    key: "check",
    value: function check() {
      var withCredentials = this.options.withCredentials;
      return this.api.requestGet(this.api.route('auth.check'), null, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }, {
    key: "login",
    value: function login(email, password) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('auth.login'), {
        email: email,
        password: password
      }, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }, {
    key: "register",
    value: function register(data) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('auth.register'), data, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }, {
    key: "logout",
    value: function logout() {
      var url = this.api.getFullUrl(this.api.route('auth.logout'));
      return fetch(url, {
        method: 'POST',
        headers: _objectSpread({}, getCSRFHeaders()),
        credentials: 'include'
      })["catch"](function (err) {
        console.log('logout err', err); // eslint-disable-line
        throw err;
      });
    }
  }, {
    key: "requestPassword",
    value: function requestPassword(email) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('auth.password.email'), {
        email: email
      }, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }, {
    key: "resetPassword",
    value: function resetPassword(data) {
      var withCredentials = this.options.withCredentials;
      return this.api.requestPost(this.api.route('auth.password.update'), data, {
        withSession: !withCredentials,
        withCredentials: withCredentials
      });
    }
  }]);
}();

var Base = /*#__PURE__*/function () {
  function Base() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Base);
    this.options = _objectSpread(_objectSpread({
      routes: {},
      generateUrl: null
    }, opts), {}, {
      baseUrl: opts.baseUrl,
      onUnauthorized: opts.onUnauthorized || null
    });
  }
  return _createClass(Base, [{
    key: "requestGet",
    value: function requestGet(path) {
      var _this = this;
      var query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      var finalQuery = query !== null ? queryString.stringify(query, {
        arrayFormat: 'bracket'
      }) : null;
      return getJSON("".concat(this.getFullUrl(path)).concat(finalQuery !== null && finalQuery.length > 0 ? "?".concat(finalQuery) : ''), _objectSpread({
        credentials: 'include',
        headers: getCSRFHeaders()
      }, opts))["catch"](function (error) {
        return _this.onError(error);
      });
    }
  }, {
    key: "requestPost",
    value: function requestPost(path, data) {
      var _this2 = this;
      var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      return postJSON(this.getFullUrl(path), data, _objectSpread({
        credentials: 'include',
        headers: getCSRFHeaders()
      }, opts))["catch"](function (error) {
        return _this2.onError(error);
      });
    }
  }, {
    key: "requestPut",
    value: function requestPut(path, data) {
      var _this3 = this;
      var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      return postJSON(this.getFullUrl(path), _objectSpread({
        _method: 'PUT'
      }, data), _objectSpread({
        credentials: 'include',
        headers: getCSRFHeaders()
      }, opts))["catch"](function (error) {
        return _this3.onError(error);
      });
    }
  }, {
    key: "requestPatch",
    value: function requestPatch(path, data) {
      var _this4 = this;
      var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      return postJSON(this.getFullUrl(path), _objectSpread({
        _method: 'PATCH'
      }, data), _objectSpread({
        credentials: 'include',
        headers: getCSRFHeaders()
      }, opts))["catch"](function (error) {
        return _this4.onError(error);
      });
    }
  }, {
    key: "requestDelete",
    value: function requestDelete(path) {
      var _this5 = this;
      return this.requestPost(this.getFullUrl(path), {
        _method: 'DELETE'
      })["catch"](function (error) {
        return _this5.onError(error);
      });
    }
  }, {
    key: "route",
    value: function route(_route, params) {
      var _this$options = this.options,
        routes = _this$options.routes,
        _this$options$generat = _this$options.generateUrl,
        generateUrl = _this$options$generat === void 0 ? null : _this$options$generat;
      if (generateUrl !== null) {
        return generateUrl(_route, params);
      }
      return generatePath(routes[_route] || _route, params);
    }
  }, {
    key: "getFullUrl",
    value: function getFullUrl(path) {
      var _this$options$baseUrl = this.options.baseUrl,
        baseUrl = _this$options$baseUrl === void 0 ? null : _this$options$baseUrl;
      var hasProtocol = path.indexOf('://') !== -1;
      return baseUrl !== null && !hasProtocol ? "".concat(baseUrl.replace(/\/$/, ''), "/").concat(path.replace(/^\//, '')) : path;
    }
  }, {
    key: "onError",
    value: function onError(err) {
      var onUnauthorized = this.options.onUnauthorized;
      var _ref = err || {},
        _ref$status = _ref.status,
        status = _ref$status === void 0 ? null : _ref$status;
      var statusCode = parseInt(status, 10);
      // If status is refused and callback exists
      if ((statusCode === 401 || statusCode === 419) && onUnauthorized !== null) {
        return onUnauthorized();
      }
      throw err;
    }
  }]);
}();

var MediasApi = /*#__PURE__*/function (_Base) {
  function MediasApi() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, MediasApi);
    return _callSuper(this, MediasApi, [{
      baseUrl: opts.baseUrl ? opts.baseUrl : null,
      routes: _objectSpread({
        index: 'medias',
        trashed: 'medias/trash',
        tags: 'medias/tags',
        show: 'medias/:media',
        store: 'medias',
        update: 'medias/:media',
        trash: 'medias/trash/:media',
        restore: 'medias/restore/:media',
        "delete": 'medias/:media',
        replace: 'medias/replace/:media'
      }, opts.routes || null)
    }]);
  }
  _inherits(MediasApi, _Base);
  return _createClass(MediasApi, [{
    key: "find",
    value: function find(id) {
      return this.requestGet(this.route('show', {
        media: id
      }));
    }
  }, {
    key: "get",
    value: function get() {
      var query = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var page = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      var count = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 10;
      var finalQuery = _objectSpread({}, query);
      if (page !== null) {
        finalQuery.page = page;
      }
      if (count !== null) {
        finalQuery.count = count;
      }
      return this.requestGet(this.route('index'), finalQuery);
    }
  }, {
    key: "getTrashed",
    value: function getTrashed() {
      var query = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var page = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      var count = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 10;
      var finalQuery = _objectSpread({}, query);
      if (page !== null) {
        finalQuery.page = page;
      }
      if (count !== null) {
        finalQuery.count = count;
      }
      return this.requestGet(this.route('trashed'), finalQuery);
    }
  }, {
    key: "getTags",
    value: function getTags() {
      var query = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var count = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 10;
      var finalQuery = _objectSpread({}, query);
      if (count !== null) {
        finalQuery.count = count;
      }
      return this.requestGet(this.route('tags'), finalQuery);
    }
  }, {
    key: "create",
    value: function create(data) {
      return this.requestPost(this.route('store'), data);
    }
  }, {
    key: "update",
    value: function update(id, data) {
      return this.requestPut(this.route('update', {
        media: id
      }), data);
    }
  }, {
    key: "trash",
    value: function trash(id) {
      return this.requestDelete(this.route('trash', {
        media: id
      }));
    }
  }, {
    key: "restore",
    value: function restore(id) {
      return this.requestPost(this.route('restore', {
        media: id
      }));
    }
  }, {
    key: "delete",
    value: function _delete(id) {
      return this.requestDelete(this.route('delete', {
        media: id
      }));
    }
  }, {
    key: "replace",
    value: function replace(id, data) {
      return this.requestPost(this.route('replace', {
        media: id
      }), data);
    }
  }]);
}(Base);

var ResourcesApi = /*#__PURE__*/function () {
  function ResourcesApi(api) {
    _classCallCheck(this, ResourcesApi);
    this.api = api;
  }
  return _createClass(ResourcesApi, [{
    key: "resourceRoute",
    value: function resourceRoute(_ref, route, params) {
      var id = _ref.id,
        _ref$has_routes = _ref.has_routes,
        hasRoutes = _ref$has_routes === void 0 ? false : _ref$has_routes;
      var routePrefix = hasRoutes ? "resources.".concat(id) : 'resources';
      return this.api.route("".concat(routePrefix, ".").concat(route), hasRoutes ? params : _objectSpread({
        resource: id
      }, params));
    }
  }, {
    key: "find",
    value: function find(resource, id) {
      return this.api.requestGet(this.resourceRoute(resource, 'show', {
        id: id
      }), null, {
        withCredentials: true
      });
    }
  }, {
    key: "get",
    value: function get(resource) {
      var query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var page = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
      var count = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 12;
      var finalQuery = _objectSpread({}, query);
      if (page !== null) {
        finalQuery.page = page;
      }
      if (count !== null) {
        finalQuery.count = count;
      }
      return this.api.requestGet(this.resourceRoute(resource, 'index'), finalQuery, {
        withCredentials: true
      });
    }
  }, {
    key: "store",
    value: function store(resource, data) {
      return this.api.requestPost(this.resourceRoute(resource, 'store'), data, {
        withCredentials: true
      });
    }
  }, {
    key: "update",
    value: function update(resource, id, data) {
      return this.api.requestPatch(this.resourceRoute(resource, 'update', {
        id: id
      }), data, {
        withCredentials: true
      });
    }
  }, {
    key: "destroy",
    value: function destroy(resource, id) {
      return this.api.requestDelete(this.resourceRoute(resource, 'destroy', {
        id: id
      }), null, {
        withCredentials: true
      });
    }
  }, {
    key: "clone",
    value: function clone(resource, id) {
      var data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      return this.api.requestPost(this.resourceRoute(resource, 'clone', {
        id: id
      }), data, {
        withCredentials: true
      });
    }
  }, {
    key: "restore",
    value: function restore(resource, id) {
      var data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      return this.api.requestPost(this.resourceRoute(resource, 'restore', {
        id: id
      }), data, {
        withCredentials: true
      });
    }
  }]);
}();

var Api = /*#__PURE__*/function (_Base) {
  function Api() {
    var _this;
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Api);
    _this = _callSuper(this, Api, [opts]);
    _this.auth = new AuthApi(_this, {
      withCredentials: true
    });
    _this.account = new AccountApi(_this, {
      withCredentials: true
    });
    _this.medias = new MediasApi(opts);
    _this.resources = new ResourcesApi(_this);
    return _this;
  }
  _inherits(Api, _Base);
  return _createClass(Api);
}(Base);

/* eslint-disable react/jsx-props-no-spreading */
var ApiContext = /*#__PURE__*/React.createContext(null);
var useApi = function useApi() {
  return useContext(ApiContext);
};
var propTypes$2 = {
  api: PropTypes.instanceOf(Api),
  baseUrl: PropTypes.string,
  onUnauthorized: PropTypes.func,
  children: PropTypes.node.isRequired
};
var defaultProps$2 = {
  api: null,
  onUnauthorized: null,
  baseUrl: undefined
};
var ApiProvider = function ApiProvider(_ref) {
  var initialApi = _ref.api,
    baseUrl = _ref.baseUrl,
    onUnauthorized = _ref.onUnauthorized,
    children = _ref.children;
  var generateUrl = useUrlGenerator();
  var previousApi = useApi();
  var api = useMemo(function () {
    return initialApi || previousApi || new Api({
      baseUrl: baseUrl,
      generateUrl: generateUrl,
      onUnauthorized: onUnauthorized
    });
  }, [previousApi, initialApi, baseUrl]);
  return /*#__PURE__*/React.createElement(ApiContext.Provider, {
    value: api
  }, children);
};
ApiProvider.propTypes = propTypes$2;
ApiProvider.defaultProps = defaultProps$2;

var useResourceDestroy = function useResourceDestroy(resource) {
  var id = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var api = useApi();
  var destroy = useCallback(function (providedId) {
    setLoading(true);
    return api.resources.destroy(resource, providedId || id).then(function (response) {
      setLoading(false);
      return response;
    })["catch"](function (e) {
      setLoading(false);
      throw e;
    });
  }, [api, resource, id, setLoading]);
  return {
    destroy: destroy,
    loading: loading
  };
};

var _excluded$1 = ["data"];
var useResourceItem = function useResourceItem(resource, id, opts) {
  var api = useApi();
  var loader = useCallback(function () {
    return api.resources.find(resource, id);
  }, [api, resource, id]);
  var _useData = useData(loader, opts),
    data = _useData.data,
    request = _objectWithoutProperties(_useData, _excluded$1);
  return _objectSpread({
    item: data
  }, request);
};

var _excluded = ["items"];
var useResourceItems = function useResourceItems(resource) {
  var query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var page = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var count = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  var opts = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : null;
  var api = useApi();
  var _ref = resource || {},
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? null : _ref$id;
  var lastResourceRef = useRef(id);
  var resourceChanging = lastResourceRef.current !== id;
  var getPage = useCallback(function () {
    var requestedPage = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    return api.resources.get(resource, query, requestedPage, count).then(function (response) {
      var _ref2 = resource || {},
        resourceId = _ref2.id;
      lastResourceRef.current = resourceId;
      return response;
    })["catch"](function (err) {
      var _ref3 = resource || {},
        resourceId = _ref3.id;
      lastResourceRef.current = resourceId;
      throw err;
    });
  }, [api, resource, query, count]);
  var getItems = useCallback(function () {
    return api.resources.get(resource, query, null, null).then(function (response) {
      var _ref4 = resource || {},
        resourceId = _ref4.id;
      lastResourceRef.current = resourceId;
      return response;
    })["catch"](function (err) {
      var _ref5 = resource || {},
        resourceId = _ref5.id;
      lastResourceRef.current = resourceId;
      throw err;
    });
  }, [api, resource, query, count]);
  var _useItemsStore = useItemsStore(id, _objectSpread({
      getPage: page !== null ? getPage : null,
      getItems: page === null ? getItems : null,
      query: query,
      page: page,
      count: count
    }, opts)),
    items = _useItemsStore.items,
    request = _objectWithoutProperties(_useItemsStore, _excluded);
  return _objectSpread({
    items: resourceChanging ? null : items
  }, request);
};

var useResourceCreate = function useResourceCreate(resource) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var api = useApi();
  var store = useCallback(function (data) {
    setLoading(true);
    return api.resources.store(resource, data).then(function (response) {
      setLoading(false);
      return response;
    })["catch"](function (e) {
      setLoading(false);
      throw e;
    });
  }, [api, resource, setLoading]);
  return {
    store: store,
    loading: loading
  };
};

var useResourceUpdate = function useResourceUpdate(resource, id) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var api = useApi();
  var update = useCallback(function (data) {
    setLoading(true);
    return api.resources.update(resource, id, data).then(function (response) {
      setLoading(false);
      return response;
    })["catch"](function (e) {
      setLoading(false);
      throw e;
    });
  }, [api, resource, id, setLoading]);
  return {
    update: update,
    loading: loading
  };
};

var useResourceClone = function useResourceClone(resource) {
  var id = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var api = useApi();
  var clone = useCallback(function (providedId) {
    setLoading(true);
    return api.resources.clone(resource, providedId || id).then(function (response) {
      setLoading(false);
      return response;
    })["catch"](function (e) {
      setLoading(false);
      throw e;
    });
  }, [api, resource, id, setLoading]);
  return {
    clone: clone,
    loading: loading
  };
};

var useResourceRestore = function useResourceRestore(resource) {
  var id = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var api = useApi();
  var restore = useCallback(function (providedId) {
    setLoading(true);
    return api.resources.restore(resource, providedId || id).then(function (response) {
      setLoading(false);
      return response;
    })["catch"](function (e) {
      setLoading(false);
      throw e;
    });
  }, [api, resource, id, setLoading]);
  return {
    restore: restore,
    loading: loading
  };
};

var QueryContext = /*#__PURE__*/React.createContext(null);
var useQueryContext = function useQueryContext() {
  return useContext(QueryContext);
};
var propTypes$1 = {
  config: PropTypes.shape({}),
  initialKey: PropTypes.arrayOf(PropTypes.string),
  initialData: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]),
  children: PropTypes.node.isRequired
};
var defaultProps$1 = {
  config: null,
  initialKey: null,
  initialData: null
};
var QueryProvider = function QueryProvider(_ref) {
  var initialConfig = _ref.config,
    initialKey = _ref.initialKey,
    initialData = _ref.initialData,
    children = _ref.children;
  var queryClient = useMemo(function () {
    var client = new QueryClient(_objectSpread({
      defaultOptions: {
        queries: {
          staleTime: Infinity
        }
      }
    }, initialConfig));
    if (initialKey !== null && initialData !== null) {
      client.setQueryData(initialKey, initialData);
    }
    return client;
  }, [initialConfig]);
  return /*#__PURE__*/React.createElement(QueryClientProvider, {
    client: queryClient
  }, children);
};
QueryProvider.propTypes = propTypes$1;
QueryProvider.defaultProps = defaultProps$1;

/* eslint-disable react/jsx-props-no-spreading */
var propTypes = {
  api: PropTypes.instanceOf(Api),
  apiBaseUrl: PropTypes.string,
  children: PropTypes.node
};
var defaultProps = {
  api: null,
  apiBaseUrl: undefined,
  children: null
};
var DataProvider = function DataProvider(_ref) {
  var api = _ref.api,
    apiBaseUrl = _ref.apiBaseUrl,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(ApiProvider, {
    api: api,
    baseUrl: apiBaseUrl
  }, children);
};
DataProvider.propTypes = propTypes;
DataProvider.defaultProps = defaultProps;

export { Api, ApiProvider, Base as BaseApi, DataProvider, QueryProvider, useApi, useData, useItems$1 as useItems, useItems as useItemsLegacy, useItemsStore, useQueryContext, useResourceClone, useResourceDestroy, useResourceItem, useResourceItems, useResourceRestore, useResourceCreate as useResourceStore, useResourceUpdate };
